import { motion } from "framer-motion";
import { MoreVertical, Trash2, Eye, Mail, Smartphone, UploadCloud, Sparkles, Camera, KeyRound, PenLine, Landmark, Inbox, FolderOpen, ArrowRightLeft, Printer, Clapperboard, Mic, Lock } from "lucide-react";
import { getCategory } from "../lib/categories";
import { fileUrl } from "../lib/api";
import { sealLabel } from "./ReleaseRules";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { tr } from "../lib/i18n";

const sourceIcon = { upload: UploadCloud, email: Mail, sms: Smartphone, text: Mail, camera: Camera, import: KeyRound, builder: PenLine, bank: Landmark, plaid: Landmark, gmail: Inbox, drive: FolderOpen, migration: ArrowRightLeft, fax: Printer };

const firstField = (doc) => {
  const entries = Object.entries(doc.extracted_fields || {});
  return entries.length ? entries[0] : null;
};

const DocumentCard = ({ doc, onInspect, onDelete, index }) => {
  const cat = getCategory(doc.category);
  const CatIcon = cat.icon;
  const SourceIcon = sourceIcon[doc.source] || UploadCloud;
  const highlight = firstField(doc);
  const date = new Date(doc.uploaded_at).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.3) }}
      data-testid="document-card"
      onClick={() => onInspect(doc)}
      className={`paper-card cursor-pointer bg-card border rounded-2xl overflow-hidden ${cat.border}`}
    >
      <div className={`h-1.5 w-full ${cat.dot}`} />
      {doc.thumbnail_path && (
        <div className="relative aspect-video bg-black overflow-hidden" data-testid="card-poster">
          <img src={`${fileUrl(doc.thumbnail_path)}?inline=1`} alt="" loading="lazy" className="h-full w-full object-cover" />
          <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] px-2 py-0.5 backdrop-blur"><Clapperboard className="h-3 w-3" /> {tr("Video")}{doc.transcribed ? " · transcript" : ""}</span>
        </div>
      )}
      {!doc.thumbnail_path && (doc.content_type || "").startsWith("audio/") && (
        <div className="flex items-center gap-2 px-4 pt-3 text-[11px] text-muted-foreground" data-testid="card-audio-badge"><Mic className="h-3.5 w-3.5 text-primary" /> {tr("Voice memo")}{doc.transcribed ? " · transcript ready" : ""}</div>
      )}
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${cat.tint}`}>
            <CatIcon className="h-5 w-5" style={{ color: cat.iconColor }} />
          </div>
          <div className="flex items-center gap-1.5">
            {doc.seal?.mode && doc.seal.mode !== "open" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-stone-800 text-stone-50 text-[10px] px-2 py-0.5" title={sealLabel(doc.seal)} data-testid="card-sealed-badge"><Lock className="h-3 w-3" /> {tr("Sealed")}</span>
            )}
            <Badge
              data-testid="card-category-badge"
              className={`${cat.badge} border-0 font-medium capitalize`}
            >
              {tr(cat.label)}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <button className="p-1 rounded-md hover:bg-accent text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem data-testid="card-inspect-btn" onClick={() => onInspect(doc)}>
                  <Eye className="h-4 w-4 mr-2" /> {tr("Inspect")}
                </DropdownMenuItem>
                {onDelete && (
                  <DropdownMenuItem
                    data-testid="card-delete-btn"
                    className="text-destructive focus:text-destructive"
                    onClick={() => onDelete(doc)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> {tr("Delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <h3 className="mt-3.5 font-semibold text-[15px] leading-snug line-clamp-2">
          {doc.title}
        </h3>
        <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2 leading-relaxed">
          {doc.match_snippet ? (
            <span data-testid="card-text-snippet" className="italic">
              <span className="not-italic font-mono text-[10px] uppercase tracking-wider text-primary mr-1">{tr("in text")}</span>
              {doc.match_snippet}
            </span>
          ) : (
            doc.summary
          )}
        </p>

        {highlight && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-muted/60 border border-border/60 px-2.5 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="text-[11px] uppercase tracking-wide font-mono text-muted-foreground">
              {highlight[0]}
            </span>
            <span className="ml-auto text-sm font-mono font-medium truncate">{highlight[1]}</span>
          </div>
        )}

        <div className="mt-3.5 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5 capitalize">
            <SourceIcon className="h-3.5 w-3.5" />
            {doc.source}
          </span>
          <span className="font-mono">{date}</span>
        </div>
      </div>
    </motion.div>
  );
};

export const DocumentGrid = ({ documents, onInspect, onDelete }) => {
  return (
    <div
      data-testid="document-grid"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      {documents.map((doc, i) => (
        <DocumentCard
          key={doc.id}
          doc={doc}
          index={i}
          onInspect={onInspect}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};
